import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { apiClient } from '../api/client';

const SlackCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');

      if (!code) {
        setStatus('error');
        setError('No authorization code received from Slack');
        setTimeout(() => navigate('/integrations/slack'), 3000);
        return;
      }

      try {
        // Exchange code for access token
        await apiClient.connectSlackWorkspace(code, state || undefined);
        
        setStatus('success');
        
        // Redirect to integrations page after 2 seconds
        setTimeout(() => {
          navigate('/integrations/slack');
        }, 2000);
      } catch (err: any) {
        console.error('Failed to complete Slack OAuth:', err);
        setStatus('error');
        setError(err.response?.data?.message || 'Failed to connect Slack workspace');
        
        // Redirect to integrations page after 3 seconds even on error
        setTimeout(() => {
          navigate('/integrations/slack');
        }, 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center max-w-md">
        {status === 'processing' && (
          <>
            <Loader2 className="h-16 w-16 animate-spin mx-auto mb-4 text-primary" />
            <h2 className="text-2xl font-semibold mb-2">Connecting Slack Workspace</h2>
            <p className="text-muted-foreground">
              Please wait while we complete the connection...
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="h-16 w-16 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-2xl font-semibold mb-2 text-green-600 dark:text-green-400">
              Successfully Connected!
            </h2>
            <p className="text-muted-foreground">
              Redirecting you back to the integrations page...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="h-16 w-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
              <XCircle className="h-10 w-10 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-2xl font-semibold mb-2 text-red-600 dark:text-red-400">
              Connection Failed
            </h2>
            <p className="text-muted-foreground mb-4">
              {error}
            </p>
            <p className="text-sm text-muted-foreground">
              Redirecting you back to try again...
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default SlackCallback;

